import React, { useEffect, useState } from "react"
import { useDispatch } from 'react-redux'
import { signIn, signOut, resetPassword, AuthError } from "aws-amplify/auth"
import { LoginInput } from "../login-input"
import { LoginButton } from "../login-button"
import { utilService } from "../../../services/util-service"
import { StoreDispatch } from '../../../store/configure-store'
import { setUserMsg } from '../../../store/user'
import { UserMsg } from "../../../models/user"
import errorIcon from '../../../assets/img/error-icon.svg'

interface EmailPasswordFormProps {
    setNextStep: React.Dispatch<any>
    setUserEmail: React.Dispatch<React.SetStateAction<string>>
}


export const EmailPasswordForm: React.FC<EmailPasswordFormProps> = ({ setNextStep, setUserEmail }) => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isCredentailsError, setIsCredentialsError] = useState(false)
    const dispatch = useDispatch<StoreDispatch>()

    useEffect(() => {
        return () => {
            setEmail("")
            setPassword("")
        }
    }, [])

    const emailValidation = (emailAddress: string) => {
        return utilService.isValidEmail(emailAddress)
    }

    const passwordValidation = (password: string) => {
        return utilService.isValidPassword(password)
    }

    useEffect(() => {
        setIsCredentialsError(false)
    }, [email, password])

    const onFormSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault()
        if (!emailValidation(email) || !passwordValidation(password)) return
        try {
            let resSignIn = await signIn({ username: email, password })
                .catch(async (data) => {
                    if (data?.name === 'UserAlreadyAuthenticatedException') {
                        await signOut()
                        const updatedSignIn = await signIn({ username: email, password })
                        return updatedSignIn
                    }
                    throw data
                })

            setUserEmail(email)
            setNextStep(resSignIn.nextStep)
        } catch (e) {
            console.error('e', e)
            setIsCredentialsError(true)
        }
    }

    const onForgotPassword = async () => {
        if (!emailValidation(email)) return
        
        try {
            const resResetPassword = await resetPassword({ username: email })
            
            const msg: UserMsg = { type: 'success', txt: 'Recovery code was sent to your email' }
            dispatch(setUserMsg({ msg }))
            
            setUserEmail(email)
            setNextStep(resResetPassword.nextStep)
        
        } catch (error) {
            const err = error as AuthError
            if (err.name === 'UserNotFoundException') {
                const msg: UserMsg = { type: 'danger', txt: 'Unfamiliar email' }
                dispatch(setUserMsg({ msg }))
                return
            }
            
            if (err.name === 'LimitExceededException') {
                const msg: UserMsg = { type: 'danger', txt: 'Attempt limit exceeded. Please try again later' }
                dispatch(setUserMsg({ msg }))
                return
            } 
            
            if (err.message === 'Cannot reset password for the user as there is no registered/verified email or phone_number') {
                const msg: UserMsg = { type: 'danger', txt: 'Your email is not varified. Please contact us' }
                dispatch(setUserMsg({ msg }))
                return
            }

            const msg: UserMsg = { type: 'danger', txt: 'Sending recovery code to your email failed' }
            dispatch(setUserMsg({ msg }))
            console.dir(err)
        }
    }

    /* We set value and changeValue from parent component to handle password manager inputs */

    return (<>
        <form onSubmit={onFormSubmit} className="email-password-form password-login-container">
            <LoginInput
                type="text"
                inputValue={email}
                onSetInput={setEmail}
                validationFunc={emailValidation}
                placeholder="Email"
                invalidMsg="Invalid email address"
            />
            <div className="password-input-container">
                <LoginInput
                    type="password"
                    inputValue={password}
                    onSetInput={setPassword}
                    validationFunc={passwordValidation}
                    placeholder="Password"
                    invalidMsg="Invalid password"
                />

                <button type="button" className="forgot-password-btn" onClick={onForgotPassword}>
                    Forgot password?
                </button>
            </div>

            <div className="login-btn-container">
                <LoginButton txt="login" action={() => null} isFullColor={true} />

                {isCredentailsError && (
                    <div className="credentials-error-msg">
                        <img src={errorIcon} alt="error" />
                        <p>Wrong credentials</p>
                    </div>
                )}
            </div>
        </form>
    </>
    )
}
